// The development bar: what state this device is in, and the two switches that
// change it. Development only — it renders behind DEV, which the build replaces
// with a literal false, so neither the bar nor anything it calls ships.
//
// It exists because both switches are invisible otherwise. Preview mode survives
// a click and fills panels with convincing invented figures — "1,644 players" is
// exactly the sort of thing that gets believed — and a saved game is why a
// finished puzzle stays finished, which is correct behaviour and maddening when
// you are trying to play the same puzzle again.
import { useEffect, useState } from 'react';
import { DEV, PREVIEW } from './preview.ts';
import './PreviewBadge.css';

/**
 * The visible height and the scroll, live.
 *
 * It is here for two jobs. **It is the figure the keyboard rule is built on** and
 * the one nobody could measure: how much screen an iPhone keyboard and its
 * accessory bar actually leave. And **it is how the jitter is seen**: a scroll
 * figure that flickers is the page arguing with itself, and a still one is not.
 * Both read off the phone rather than being estimated from a desktop.
 */
let lastRows = '';

function Viewport() {
  const [state, setState] = useState(() => ({ h: 0, y: 0 }));
  useEffect(() => {
    const vv = window.visualViewport;
    const read = () => setState({
      h: Math.round(vv ? vv.height : window.innerHeight),
      y: Math.round(window.scrollY)
    });
    read();
    vv?.addEventListener('resize', read);
    vv?.addEventListener('scroll', read);
    window.addEventListener('scroll', read, { passive: true });
    return () => {
      vv?.removeEventListener('resize', read);
      vv?.removeEventListener('scroll', read);
      window.removeEventListener('scroll', read);
    };
  }, []);
  // The row count the list has been fitted to, which is the thing being judged
  // on a phone and cannot be read off a desktop. **It keeps the last figure the
  // keyboard produced**, because sideways the bar is in the header, which has
  // scrolled away by the time the keyboard is up: the reading is taken after.
  const control = typeof document === 'undefined' ? null : document.querySelector('.cs-control');
  const now = control ? getComputedStyle(control).getPropertyValue('--cs-rows').trim() : '';
  if (now) lastRows = now;
  const rows = now || lastRows;
  return <span className="dev-state">{state.h}h {state.y}y{rows ? ` ${rows}r` : ''}</span>;
}

export function PreviewBadge() {
  if (!DEV) return null;
  // Each part is its own element, separators included, so that sideways the
  // bar can lay them out as a small grid in the header (PreviewBadge.css).
  return (
    <p className={`dev-bar${PREVIEW ? ' is-previewing' : ''}`}>
      {/* The state and the switch in one: the link says what is on and goes
          to the other. */}
      {PREVIEW
        ? <a href="?preview=off" title="Invented figures are showing. Turn them off.">Preview: on</a>
        : <a href="?preview" title="Fill the empty panels with invented figures">Preview: off</a>}
      <span className="dev-sep"> · </span>
      <Viewport />
      <span className="dev-sep"> · </span>
      {/* A link, not a button: it reloads, and a fresh page is the only way the
          board forgets a game that is already on screen. */}
      <a href="?reset" title="Clear this device's saved games and stats, and start again as a new player">Reset</a>
    </p>
  );
}
